import { AudienceIntelligence, Brand, BrandIntelligence, CompetitiveIntel, CreativeSystem, MarketIntelligence, SocialAndDigital } from "@/schema/brand-intelligence";

const brand: Brand = {
  name: "Outdoor America Holdings",
  industry: ["Streaming Media", "E-Commerce"],
  sub_industry: ["Outdoor Sports Streaming Platform", "Outdoor Sporting Goods Retail"],
  founded_year: 1922,
  founded_original_name: "Izaak Walton Monthly",
  headquarters: ["Blue Springs, Missouri, United States"],
  tagline_primary: "Inspiring Americans to spend more time in our nation's great outdoors.",
  mission: '"Outdoor America inspires people to get outside with premium hunting, fishing, and adventure on every device."',
  vision: '"To become the leading outdoor lifestyle network, inspiring people to explore nature, everywhere."',
  overview:
    "Outdoor America is a multi-platform outdoor lifestyle network delivering hunting, fishing, and adventure content across broadcast TV, FAST channels, and streaming apps. It also runs an e-commerce marketplace and produces originals like Goliath Fishing and Tejas Deer Camp nationwide.",
  scale: {
    revenue: 0,
    employees_estimate: 0,
    markets_served_count: 1,
    operating_countries_count: 1,
  },
  global_presence: {
    regions: [
      {
        region: "North America",
        headquarters: "Blue Springs, Missouri, United States",
        active_markets: ["United States"],
        market_type: "Direct (Streaming/FAST TV), Broadcast Distribution, E-Commerce",
      },
    ],
    key_markets_by_revenue_share: [
      {
        region: "United States",
        share_percent: 100,
        notes:
          "Primary market (100% of disclosed operations). The company's syndicated programming is distributed to over 86 million TV households in the United States as of November 2024, indicating substantial domestic market penetration.",
      },
    ],
  },
};

const creativeSystem: CreativeSystem = {
  visual_identity: {
    colors: {
      primary: [
        {
          name: "Forest Green",
          hex: "#2D5016",
        },
        {
          name: "Deep Navy",
          hex: "#1A2B3C",
        },
        {
          name: "Charcoal Black",
          hex: "#2B2B2B",
        },
      ],
      secondary: [
        {
          name: "Earth Brown",
          hex: "#6B4423",
        },
        {
          name: "Stone Gray",
          hex: "#7A7A7A",
        },
      ],
      accent: [
        {
          name: "Action Orange",
          hex: "#FF6B35",
        },
        {
          name: "Sky Blue",
          hex: "#4A90E2",
        },
      ],
    },
    typography: [
      {
        font_family: "Sans-serif system font (likely Helvetica Neue or Arial)",
        weight: "Regular (400), Medium (500), Bold (700)",
        usage: "Headlines, navigation, body text",
      },
      {
        font_family: "Roboto or similar web-safe sans-serif",
        weight: "Regular (400), Medium (500)",
        usage: "Body copy, product descriptions on e-commerce platform",
      },
    ],
    logos: [
      {
        variant: "Primary Horizontal Wordmark",
        url: "https://assets.zocket.com/uploads/21c5beb7-c519-4c03-8073-3b82ad1b905f-OA-logo.avif",
      },
    ],
  },
  verbal_identity: {
    voice_traits: ["Inspirational", "Authoritative", "Accessible", "Authentic", "Patriotic", "Educational", "Community-Oriented"],
    taglines: [
      "Inspiring Americans to spend more time in our Nation's great outdoors",
      "Outdoor America is the ultimate streaming platform for outdoor enthusiasts",
      "Outdoor America is the destination for every adventurer",
      "Your digital home for the great outdoors",
      "Together, we're building a world-class outdoor lifestyle network with premium programming, dynamic partnerships, and a global vision",
    ],
    messaging_dos: [],
    messaging_donts: [],
    banned_words: [],
  },
  creative_assets: ["https://assets.zocket.com/uploads/01aec0ba-2ab6-4726-8c2c-e91034819a28-IMG_4213.jpg", "https://assets.zocket.com/uploads/860f8064-25b8-4628-97c6-ffb4b4fbf004-IMG_4215.jpg"],
  compliance: {
    brand_safety_guidelines: [],
    copyright_trademark_usage: [],
  },
  brand_architecture: {
    available_brands: ["Outdoor America", "Outdoor America Pro Shop", "Field & Stream TV"],
    selected_brand: "Outdoor America",
  },
  ai_creative_memory: {
    design_memory: {
      pattern_accuracy: "87%",
      patterns_learned: 156,
      avg_generation: "1.8s",
    },
    style_drift_alerts: [
      {
        title: "Goliath Fishing S3 Thumbnail",
        description: "Logo placement inconsistent with brand guidelines",
        severity: "medium",
      },
      {
        title: "Tejas Deer Camp Promo",
        description: "Font weight lighter than standard brand typography",
        severity: "low",
      },
      {
        title: "FAST Channel Banner",
        description: "Green accent deviates from brand palette #2D5016",
        severity: "high",
      },
    ],
    preference_learning: [
      { preference: "Outdoor Adventure", score: 91, change: 5 },
      { preference: "Rustic & Natural", score: 87, change: 3 },
      { preference: "Green Accents", score: 84, change: 4 },
      { preference: "Action & Energy", score: 79, change: 6 },
    ],
  },
};

const marketIntelligence: MarketIntelligence = {
  kpis: {
    sentiment: 7.2,
    sentiment_graph: [
      {
        metric: "Sentiment Score",
        value: "7.2/10",
        delta: "+0.5",
      },
      {
        metric: "Estimated NPS",
        value: "~30",
        delta: "N/A",
      },
      {
        metric: "Broadcast Market Share",
        value: "0.18%",
        delta: "+0.03%",
      },
      {
        metric: "Weekly Engagement Rate",
        value: "0.18%",
        delta: "+0.02%",
      },
      {
        metric: "Brand Awareness (Outdoor Streaming)",
        value: "~8-12%",
        delta: "+3%",
      },
      {
        metric: "E-Commerce Product Range",
        value: "50K+ SKUs",
        delta: "+7.5K",
      },
      {
        metric: "Distribution Growth",
        value: "86M→90M HH",
        delta: "+4.7%",
      },
      {
        metric: "Share of Voice (Outdoor Media)",
        value: "~5-8%",
        delta: "+2%",
      },
    ],
  },
  sentiment_summary: {
    time_range: ["Last 12 months (2024-2025)"],
    themes: {
      positive: [
        "Strategic Partnership Strength: Field & Stream partnership unites Field & Stream's 150-year heritage with Outdoor America's rapidly growing television network",
        "Broad Distribution Reach: Reaches approximately 84 million TV households nationwide plus additional 20 million households through Regional Sports Networks",
        "Authentic Outdoor Content: Focus on inspiring Americans to spend time outdoors with quality programming",
        "Multi-Platform Accessibility: Available across broadcast TV, FAST channels, streaming devices, and e-commerce",
        "Targeted Audience Engagement: Strong viewership in states with high concentrations of licensed hunters and anglers including Florida, Georgia, Indiana, Iowa, Kansas, Missouri, North Carolina, Ohio, Tennessee, and Texas",
        "Comprehensive E-Commerce Offering: Over 800 brands and 50,000 products for outdoor enthusiasts",
        "Industry-Leading Reach: Delivers 150,000 viewing households per single airing—3-5x more passionate enthusiast viewers than any other outdoor media entity",
      ],
      neutral: [
        "Limited Brand Recognition: Relatively new brand (launched 2017) still building awareness versus established competitors",
        "E-Commerce Site Legitimacy Questions: The shop.outdooramerica.com review has been searched 159 times, indicating some consumer verification behavior",
        "Evolving Business Model: Transition from standalone brand to Field & Stream TV partnership creates uncertainty",
        "Competitive Positioning: Operating in crowded outdoor streaming market with MyOutdoorTV, Mossy Oak GO, and others",
        "Limited Public Reviews: Minimal customer feedback available on major review platforms",
      ],
      negative: [
        "Low Online Visibility: The Tranco ranking of this website is low, which can be considered low in relation to other websites from the website's country",
        "Limited Customer Reviews: Amazon app has only 4.3 out of 5 stars with 12 customer ratings",
        "E-Commerce Trust Concerns: Users report never having seen/heard about the e-commerce site and difficulty finding company information",
        "Pricing Competitiveness: Limited mentions of competitive pricing versus established outdoor retailers",
        "Content Delay Concerns: Potential lag between broadcast and streaming availability (common industry issue)",
        "Market Fragmentation: Competing against free YouTube content and established subscription services",
        "Limited Product Differentiation: Challenges distinguishing from MyOutdoorTV and other outdoor streaming platforms",
      ],
    },
  },
  distribution: [
    { brand: "MyOutdoorTV (Outdoor Sportsman Group)", share: 35 },
    { brand: "YouTube (Free Outdoor Content)", share: 28 },
    { brand: "Mossy Oak GO", share: 12 },
    { brand: "Outdoor America / Field & Stream TV", share: 8 },
    { brand: "Others", share: 17 },
  ],
  sentiment_by_product: [
    { product: "Broadcast TV Syndication", sentiment: 7.5, change_delta: 8 },
    { product: "FAST Channel (Field & Stream TV)", sentiment: 7.8, change_delta: 15 },
    { product: "Streaming App", sentiment: 6.8, change_delta: -2 },
    { product: "E-Commerce Pro Shop", sentiment: 6.5, change_delta: 0 },
    { product: "Original Series Content", sentiment: 7.2, change_delta: 5 },
    { product: "Regional Sports Network Distribution", sentiment: 7.4, change_delta: 10 },
    { product: "Digital/Social Platforms", sentiment: 6.9, change_delta: 3 },
  ],
  top_topics: {
    praise: [
      { topic: "Massive Broadcast Reach", weight: 10 },
      { topic: "Authentic Outdoor Focus", weight: 10 },
      { topic: "Strategic Partnerships", weight: 10 },
      { topic: "Geographic Targeting", weight: 7 },
      { topic: "Multi-Platform Availability", weight: 7 },
      { topic: "Comprehensive Product Selection", weight: 7 },
      { topic: "Consistent Weekly Programming", weight: 7 },
    ],
    complaints: [
      { topic: "Limited Brand Awareness", weight: 10 },
      { topic: "E-Commerce Trust Issues", weight: 10 },
      { topic: "Competitive Pricing Concerns", weight: 7 },
      { topic: "Limited Product Selection", weight: 7 },
      { topic: "Fragmented User Experience", weight: 7 },
      { topic: "Content Discovery Challenges", weight: 7 },
      { topic: "Limited Customer Reviews", weight: 7 },
    ],
  },
};

const competitiveIntel: CompetitiveIntel = {
  primary_competitors: [
    {
      name: "MyOutdoorTV (Outdoor Sportsman Group)",
      threat_level: "high",
      market_share: 37.5,
      sentiment: 72,
    },
    {
      name: "Sportsman Channel (Kroenke Sports & Entertainment)",
      threat_level: "high",
      market_share: 17.5,
      sentiment: 68,
    },
    {
      name: "Outdoor Channel (Kroenke Sports & Entertainment)",
      threat_level: "high",
      market_share: 15,
      sentiment: 65,
    },
    {
      name: "Pursuit Channel",
      threat_level: "medium",
      market_share: 10,
      sentiment: 58,
    },
    {
      name: "CarbonTV",
      threat_level: "medium",
      market_share: 6.5,
      sentiment: 62,
    },
    {
      name: "Field & Stream TV (Outdoor America Partnership)",
      threat_level: "low",
      market_share: 0,
      sentiment: 70,
    },
  ],
  secondary_competitors: [
    {
      name: "ESPN+ (Disney)",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "YouTube (Google)",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Amazon Prime Video",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Netflix",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Discovery+ / Max (Warner Bros. Discovery)",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
  ],
  share_of_voice: [
    {
      channel: "Social Media",
      distribution: {
        MyOutdoorTV: 25,
        "Outdoor Channel": 20,
        "Sportsman Channel": 18,
        "Outdoor America": 12,
        "Pursuit Channel": 10,
        CarbonTV: 8,
        Others: 7,
      },
    },
    {
      channel: "Digital/Streaming Advertising",
      distribution: {
        MyOutdoorTV: 30,
        "Outdoor Channel": 22,
        "Sportsman Channel": 20,
        "Outdoor America": 10,
        "Pursuit Channel": 8,
        CarbonTV: 6,
        Others: 4,
      },
    },
    {
      channel: "Traditional TV/Broadcast",
      distribution: {
        "Outdoor Channel": 28,
        "Sportsman Channel": 25,
        "Pursuit Channel": 18,
        "Outdoor America": 15,
        MyOutdoorTV: 8,
        Others: 6,
      },
    },
    {
      channel: "Search/SEO",
      distribution: {
        MyOutdoorTV: 28,
        "Outdoor Channel": 24,
        "YouTube (Outdoor Content)": 20,
        "Sportsman Channel": 15,
        "Outdoor America": 8,
        Others: 5,
      },
    },
  ],
  competitive_benchmarking: [
    {
      metric_name: "Brand Awareness Score 2025",
      brands: [
        { brand_name: "MyOutdoorTV", score: 68 },
        { brand_name: "Outdoor Channel", score: 72 },
        { brand_name: "Sportsman Channel", score: 70 },
        { brand_name: "Outdoor America", score: 42 },
        { brand_name: "Pursuit Channel", score: 48 },
        { brand_name: "CarbonTV", score: 35 },
        { brand_name: "Field & Stream TV", score: 65 },
      ],
    },
    {
      metric_name: "Customer Satisfaction Index 2025",
      brands: [
        { brand_name: "MyOutdoorTV", score: 74 },
        { brand_name: "Outdoor America", score: 68 },
        { brand_name: "Sportsman Channel", score: 70 },
        { brand_name: "Outdoor Channel", score: 69 },
        { brand_name: "CarbonTV", score: 72 },
        { brand_name: "Pursuit Channel", score: 62 },
        { brand_name: "YouTube TV (Benchmark)", score: 85 },
      ],
    },
    {
      metric_name: "Content Quality & Variety Score 2025",
      brands: [
        { brand_name: "MyOutdoorTV", score: 82 },
        { brand_name: "Outdoor America", score: 74 },
        { brand_name: "Sportsman Channel", score: 76 },
        { brand_name: "Outdoor Channel", score: 78 },
        { brand_name: "CarbonTV", score: 72 },
        { brand_name: "Pursuit Channel", score: 65 },
        { brand_name: "YouTube TV (Benchmark)", score: 85 },
      ],
    },
    {
      metric_name: "Streaming Reliability & Technical Performance 2025",
      brands: [
        { brand_name: "MyOutdoorTV", score: 76 },
        { brand_name: "Outdoor America", score: 72 },
        { brand_name: "Sportsman Channel", score: 70 },
        { brand_name: "Outdoor Channel", score: 71 },
        { brand_name: "CarbonTV", score: 68 },
        { brand_name: "Pursuit Channel", score: 64 },
        { brand_name: "YouTube TV (Benchmark)", score: 85 },
      ],
    },
    {
      metric_name: "Price-to-Value Ratio Score 2025",
      brands: [
        { brand_name: "CarbonTV", score: 85 },
        { brand_name: "Outdoor America", score: 70 },
        { brand_name: "MyOutdoorTV", score: 68 },
        { brand_name: "Pursuit Channel", score: 72 },
        { brand_name: "Outdoor Channel", score: 65 },
        { brand_name: "Sportsman Channel", score: 64 },
        { brand_name: "YouTube TV (Benchmark)", score: 85 },
      ],
    },
    {
      metric_name: "Net Trust Quotient Score 2025",
      brands: [
        { brand_name: "Field & Stream (Brand)", score: 78 },
        { brand_name: "Outdoor Channel", score: 72 },
        { brand_name: "Sportsman Channel", score: 70 },
        { brand_name: "MyOutdoorTV", score: 68 },
        { brand_name: "Outdoor America", score: 58 },
        { brand_name: "Pursuit Channel", score: 60 },
        { brand_name: "CarbonTV", score: 62 },
        { brand_name: "YouTube TV (Benchmark)", score: 85 },
      ],
    },
  ],
  positioning: {
    competitive_advantages: [
      "Broadcast Reach: 86 million TV households provides significant distribution advantage",
      "E-commerce Integration: 800+ brands and 50,000+ products in Pro Shop creates revenue diversification",
      "Field & Stream Partnership: Access to 150-year heritage brand and established audience",
      "Original Content: Shows like Goliath Fishing and Tejas Deer Camp differentiate from aggregated content",
    ],
    improvement_areas: [
      "Social Media Presence: Significantly behind competitors in follower counts across all platforms",
      "Brand Awareness: Lower recognition compared to established players (42 vs. 68-72 for leaders)",
      "Subscriber Base: Need to build paid subscriber base to compete with MyOutdoorTV's 400,000 subscribers",
      "Share of Voice: Currently ~10-15% across channels vs. 25-30% for market leaders",
    ],
    disruption_watchlist: ["Waypoint TV", "The Hunting Public", "GoWild", "Midwest Whitetail / Whitetail TV", "Bowhunt or Die"],
  },
};

const audienceIntelligence: AudienceIntelligence = {
  audience_summary:
    "Outdoor America's audience primarily consists of individuals aged 45 and older, with a significant portion of the audience being male. They are conservation-minded, tradition-oriented, and value-conscious, with strong interests in hunting, fishing, and outdoor recreation. The audience prefers shopping in physical stores but also engages with online platforms for research and purchase. They are active on social media, particularly YouTube and Facebook, and value community and authenticity in their brand choices.",
  core_demographics: {
    primary_age_range: "45-54",
    gender_split: {
      male: 54,
      female: 46,
    },
    income_median_usd: 70000,
    location_type: "Suburban",
    demographics_distribution: {
      age: [
        { age_range: "18-24", percentage: 24 },
        { age_range: "25-34", percentage: 34 },
        { age_range: "35-44", percentage: 22 },
        { age_range: "45-54", percentage: 12 },
        { age_range: "55+", percentage: 8 },
      ],
      education: [
        { education_level: "Bachelor's+", percentage: 48 },
        { education_level: "Some College", percentage: 28 },
        { education_level: "Graduate Degree", percentage: 18 },
        { education_level: "High School", percentage: 6 },
      ],
      geographic: [
        { location: "Suburban", percentage: 55 },
        { location: "Urban", percentage: 35 },
        { location: "Rural", percentage: 10 },
      ],
    },
  },
  occupation_distribution: [
    { occupation: "Hunters", percentage: 40 },
    { occupation: "Fishers", percentage: 30 },
    { occupation: "Outdoor Enthusiasts", percentage: 20 },
    { occupation: "Outdoor Professionals", percentage: 10 },
  ],
  interests: {
    top_categories: [
      { category: "Hunting & Shooting Sports", score: 9 },
      { category: "Fishing (Freshwater & Saltwater)", score: 9 },
      { category: "Camping & Outdoor Recreation", score: 8 },
      { category: "Wildlife & Conservation", score: 8 },
      { category: "Boating & Marine Activities", score: 7 },
      { category: "Hiking & Trail Activities", score: 7 },
      { category: "Outdoor Cooking & Food Preparation", score: 7 },
      { category: "Sports & Fitness", score: 6 },
      { category: "DIY & Home Improvement", score: 6 },
      { category: "Outdoor Media & Entertainment", score: 8 },
    ],
    brand_affinities: [
      { brand: "Bass Pro Shops", affinity: 85 },
      { brand: "Cabela's", affinity: 82 },
      { brand: "Yeti", affinity: 78 },
      { brand: "Shimano", affinity: 72 },
      { brand: "Columbia", affinity: 68 },
      { brand: "Sitka", affinity: 65 },
      { brand: "Mossy Oak", affinity: 70 },
      { brand: "Patagonia", affinity: 65 },
      { brand: "Field & Stream", affinity: 75 },
      { brand: "Ducks Unlimited / NWTF / RMEF", affinity: 60 },
    ],
  },
  behavioral_insights: {
    shopping_behavior: {
      purchase_channel_distribution: {
        "Retail/In-store": 58,
        "Online/E-commerce": 28,
        "Catalog/Direct mail": 8,
        "Pro shops/Specialty dealers": 6,
      },
      purchase_frequency: "2.3 times per year",
      avg_order_value_usd: 102.35,
    },
    decision_journey: {
      stages: [
        {
          stage: 1,
          name: "Content Discovery",
          description: "Watch outdoor TV shows, YouTube videos, social media content; read magazines",
          duration_days: 60,
        },
        {
          stage: 2,
          name: "Product Research & Comparison",
          description: "Reviews, comparisons, specs; customer reviews play critical role; brand ambassadors and influencers",
          duration_days: 22,
        },
        {
          stage: 3,
          name: "In-Store Evaluation",
          description: "Physical inspection, test products, receive assistance from knowledgeable staff",
          duration_days: 10,
        },
        {
          stage: 4,
          name: "Transaction & Loyalty Enrollment",
          description: "Purchase with CLUB card, earn points, redeem for merchandise or outdoor adventures",
          duration_days: 2,
        },
        {
          stage: 5,
          name: "Usage & Community Sharing",
          description: "38% share catches on digital platforms; 12% use public social media",
          duration_days: 0,
        },
      ],
    },
    media_and_device_usage: {
      device_preference: {
        "Smart TV/Connected TV": 42,
        "Mobile/Smartphone": 35,
        "Desktop/Laptop": 15,
        Tablet: 8,
      },
      media_channels: {
        YouTube: 32,
        Facebook: 28,
        Instagram: 18,
        "Streaming TV (FAST/OTT)": 12,
        "Traditional TV/Cable": 6,
        "Magazines/Print": 3,
        Podcasts: 1,
      },
    },
  },
  platforms: {
    distribution: {
      YouTube: 32,
      Facebook: 28,
      Instagram: 18,
      "Streaming TV (FAST/OTT)": 12,
      "Traditional TV/Cable": 6,
      "Magazines/Print": 3,
      Podcasts: 1,
    },
  },
};

const socialAndDigital: SocialAndDigital = {
  platform_metrics: [
    { platform: "Instagram", followers: 10000, health_score: 60 },
    { platform: "YouTube", followers: 0, health_score: 0 },
    { platform: "Facebook", followers: 0, health_score: 0 },
    { platform: "LinkedIn", followers: 0, health_score: 0 },
    { platform: "Twitter/X", followers: 0, health_score: 0 },
    { platform: "TikTok", followers: 0, health_score: 0 },
    { platform: "Broadcast TV & FAST Channels", followers: 84000000, health_score: 85 },
    { platform: "Outdoor America Pro Shop", followers: 0, health_score: 85 },
  ],
  sentiment_distribution: [
    { label: "Positive", percentage: 40 },
    { label: "Neutral", percentage: 40 },
    { label: "Negative", percentage: 20 },
  ],
  recent_comments: [],
  engagement: {
    engagement_by_platform: [
      { platform: "Instagram", engagement_rate: 34 },
      { platform: "Broadcast & FAST TV", engagement_rate: 17.3 },
      { platform: "YouTube", engagement_rate: 0 },
      { platform: "Facebook", engagement_rate: 0 },
    ],
    best_performing_content_types: [
      { content_type: "Original Series & Episodic Content", performance: 9 },
      { content_type: "Instagram Reels & Short Video", performance: 9 },
      { content_type: "UGC & Influencer Content", performance: 8 },
      { content_type: "Expert Tips & Instructional", performance: 7 },
      { content_type: "Outdoor Lifestyle Stories", performance: 9 },
      { content_type: "Live Hunting/Fishing Events", performance: 7 },
      { content_type: "Documentary-Style Content", performance: 6 },
    ],
    follower_growth_trend: [
      { month: "Jan", followers: 2123177 },
      { month: "July 2025", followers: 213334 },
      { month: "August 2025", followers: 2123567 },
      { month: "September 2025", followers: 2673177 },
      { month: "October 2025", followers: 2123456 },
      { month: "November 2025", followers: 2123437 },
      { month: "December 2025", followers: 2128627 },
    ],
  },
};

export const mBrandIntelligenceOutdoorAmerica: BrandIntelligence = {
  brand,
  creative_system: creativeSystem,
  market_intelligence: marketIntelligence,
  competitive_intel: competitiveIntel,
  audience_intelligence: audienceIntelligence,
  social_and_digital: socialAndDigital,
};
